import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CaptionChatMessage = {
  role?: string;
  text?: string;
  at?: string;
  kind?: string;
};

type Props = {
  caption: string;
  engage?: string;
  fbEnding: string;
  igEnding: string;
  messages: CaptionChatMessage[];
  verifyCta: boolean;
  busy?: boolean;
  canUndo?: boolean;
  disabled?: boolean;
  error?: string;
  onCaptionChange: (next: string) => void;
  onCaptionCommit?: (next: string) => void;
  onVerifyCta: (next: boolean) => void;
  onSend: (message: string) => void;
  onUndo: () => void;
  undoPending?: boolean;
  showCorpus?: boolean;
  showVerify?: boolean;
  title?: string;
  hint?: string;
  placeholder?: string;
  busyLabel?: string;
};

export default function CaptionChatPanel({
  caption,
  engage,
  fbEnding,
  igEnding,
  messages,
  verifyCta,
  busy,
  canUndo,
  disabled,
  error,
  onCaptionChange,
  onCaptionCommit,
  onVerifyCta,
  onSend,
  onUndo,
  undoPending,
  showCorpus = true,
  showVerify = true,
  title = "Czat o opisie",
  hint = "Napisz, co zmienić. Enter wysyła. Korpus edytujesz powyżej wątku.",
  placeholder,
  busyLabel = "Piszę opis…",
}: Props) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const hasCaption = Boolean(caption.trim());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy, caption]);

  const submit = () => {
    const text = draft.trim();
    if (busy || disabled) return;
    if (!text && hasCaption) return;
    onSend(text);
    setDraft("");
  };

  return (
    <div className="flex min-h-[min(42rem,calc(100vh-8rem))] flex-col rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div>
          <div className="text-sm font-medium text-slate-900">{title}</div>
          <p className="text-[11px] text-muted-foreground">{hint}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled || busy || !canUndo || undoPending}
          onClick={onUndo}
        >
          {undoPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Cofnij
        </Button>
      </div>

      {showCorpus ? (
        <div className="space-y-2 border-b border-border px-3 py-3">
          <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Korpus (draft)
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-md border border-input bg-transparent p-2 text-sm"
            value={caption}
            disabled={disabled || busy}
            placeholder="Scena i rozwinięcie. Bez URL i bez hasła w komentarzu."
            onChange={(e) => onCaptionChange(e.target.value)}
            onBlur={(e) => onCaptionCommit?.(e.target.value)}
          />
          {engage ? (
            <p className="text-xs text-muted-foreground">Pytanie do komentarza: {engage}</p>
          ) : null}
          <div className="grid gap-2 text-[11px] text-slate-700 sm:grid-cols-2">
            <p className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
              <span className="font-medium text-slate-900">FB · </span>
              <span className="whitespace-pre-wrap text-muted-foreground">{fbEnding}</span>
            </p>
            <p className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
              <span className="font-medium text-slate-900">IG · </span>
              <span className="whitespace-pre-wrap text-muted-foreground">{igEnding}</span>
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {messages.length === 0 && !busy ? (
          <p className="text-sm text-muted-foreground">
            {placeholder ||
              (hasCaption
                ? "Np. „mniej powtórzeń ze ścian, więcej o progu domu”."
                : "Wyślij pustą wiadomość albo napisz kąt, żebym napisała pierwszy opis.")}
          </p>
        ) : null}
        {messages.map((msg, i) => {
          const mine = (msg.role || "") === "user";
          const cta = msg.kind === "caption_cta";
          return (
            <div
              key={`${msg.at || i}-${msg.role || "x"}`}
              className={cn("flex", mine ? "justify-end" : "justify-start")}
            >
              <div
                className={cn(
                  "max-w-[92%] rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
                  mine ? "bg-slate-900 text-white" : "bg-muted/50 text-foreground",
                  cta && "border border-violet-200 bg-violet-50/80 text-violet-950",
                  msg.kind === "undo" && "border border-amber-200 bg-amber-50/80",
                )}
              >
                {cta ? (
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-violet-900">
                    Książki
                  </div>
                ) : null}
                {msg.text}
              </div>
            </div>
          );
        })}
        {busy ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {busyLabel}
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>

      {error ? <p className="px-3 pb-1 text-xs text-destructive">{error}</p> : null}

      <div className="space-y-2 border-t border-border p-3">
        <div className="flex gap-2">
          <textarea
            className="min-h-[64px] flex-1 resize-y rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
            placeholder={
              hasCaption
                ? "Napisz, co zmienić w opisie…"
                : "Kąt opisu (opcjonalnie) albo Enter, żeby napisać pierwszy…"
            }
            value={draft}
            disabled={disabled || busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Button
            type="button"
            size="icon"
            disabled={disabled || busy || (!draft.trim() && hasCaption)}
            onClick={submit}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {showVerify ? (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={verifyCta}
              disabled={disabled || busy}
              onChange={(e) => onVerifyCta(e.target.checked)}
            />
            Sprawdź, czy to jest w książkach
          </label>
        ) : null}
      </div>
    </div>
  );
}
